import { InputAdornment, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { SelectInput } from '@/components/form/baseInputs/SelectInput';
import { Briefcase02Icon } from '@/components/icons/Briefcase02Icon';
import { Card } from '@/components/layout/Card/Card';
import { Grid } from '@/components/layout/Grid';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useDefaultDisclaimerConfiguration } from '@/hooks/useDefaultDisclaimers';
import { useReportError } from '@/hooks/useReportError';
import { useSubBrandOptions } from '@/modules/admin/branding/hooks/useSubBrandOptions';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { getNodes } from '@/utils/graphqlUtils';

import { useAdminLegalDisclaimersFormData } from './AdminLegalDisclaimersPage.hooks';
import {
  getCreateOrganizationDisclaimersInput,
  getCreateSubBrandDisclaimersInput,
  getFormDisclaimers,
  getUpdateOrganizationDisclaimersInput,
  getUpdateSubBrandDisclaimersInput,
  getUpdateTenantDisclaimersInput,
} from './AdminLegalDisclaimersPage.utils';
import {
  Operations,
  useCreateOrganizationLegalDisclaimersMutation,
  useCreateSubBrandLegalDisclaimersMutation,
  useUpdateLegalDisclaimersMutation,
  useUpdateOrganizationLegalDisclaimersMutation,
  useUpdateSubBrandLegalDisclaimersMutation,
} from './graphql/AdminLegalDisclaimers.generated';
import {
  LegalDisclaimersForm,
  LegalDisclaimersFormShape,
} from './LegalDisclaimersForm';

export function AdminLegalDisclaimersPage() {
  const defaultDisclaimerConfiguration = useDefaultDisclaimerConfiguration();
  const { createSuccessFeedback, showFeedback } = useFeedback();
  const { applicationConfiguration } = useTenantDetailsContext();
  const [selectedActiveBrandId, setSelectedActiveBrand] = useState<string>('');
  const { reportError } = useReportError();
  const {
    organizationDisclosures,
    tenantDisclosures,
    subBrandDisclosuresMap,
    queryResponse,
  } = useAdminLegalDisclaimersFormData();

  const subBrands = useMemo(
    () => getNodes(queryResponse.data?.subBrands),
    [queryResponse.data?.subBrands]
  );

  const subBrandOptions = useSubBrandOptions(subBrands);
  const canEditOrganizationDisclaimers = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MANAGE_ORGANIZATION_DISCLAIMERS
  );
  const isOrganizationManaged =
    applicationConfiguration?.disclaimerConfigurationOverridesDescendants ===
    true;
  const isInitialLoading =
    (queryResponse.loading && !queryResponse.data) || !applicationConfiguration;
  const isReadOnly =
    isInitialLoading ||
    !!queryResponse.error ||
    (isOrganizationManaged && !canEditOrganizationDisclaimers);

  const successMessage = useMemo(() => {
    if (isOrganizationManaged) {
      return 'Successfully updated organization legal disclaimers';
    }

    const subBrandName: string | undefined = selectedActiveBrandId
      ? subBrands.find((subBrand) => subBrand.id === selectedActiveBrandId)
          ?.displayName
      : undefined;

    if (subBrandName) {
      return `Successfully updated legal disclaimers for ${subBrandName}`;
    }

    return 'Successfully updated legal disclaimers';
  }, [isOrganizationManaged, selectedActiveBrandId, subBrands]);

  const [updateTenantDisclaimers] = useUpdateLegalDisclaimersMutation({
    onCompleted: createSuccessFeedback(successMessage),
    onError: (err) => {
      showFeedback(
        'There was an error updating the legal disclaimers. Please try again.'
      );
      reportError('failed to update legal disclaimers', err);
    },
    update: (cache) => {
      cache.evict({
        id: cache.identify({
          __typename: 'TenantDisclaimerConfiguration',
        }),
      });
      cache.evict({
        id: cache.identify({
          __typename: 'SubBrand',
          id: selectedActiveBrandId,
        }),
      });
    },
  });

  const [updateSubBrandDisclaimers] = useUpdateSubBrandLegalDisclaimersMutation(
    {
      onCompleted: createSuccessFeedback(successMessage),
      onError: (err) => {
        showFeedback(
          'There was an error updating the legal disclaimers. Please try again.'
        );
        reportError('failed to update legal disclaimers', err);
      },
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            __typename: 'TenantDisclaimerConfiguration',
          }),
        });
        cache.evict({
          id: cache.identify({
            __typename: 'SubBrand',
            id: selectedActiveBrandId,
          }),
        });
      },
    }
  );

  const [createSubBrandDisclaimers] = useCreateSubBrandLegalDisclaimersMutation(
    {
      onCompleted: createSuccessFeedback(successMessage),
      onError: (err) => {
        showFeedback(
          'There was an error updating the legal disclaimers. Please try again.'
        );
        reportError('failed to update legal disclaimers', err);
      },
      update: (cache) => {
        cache.evict({
          id: cache.identify({
            __typename: 'TenantDisclaimerConfiguration',
          }),
        });
        cache.evict({
          id: cache.identify({
            __typename: 'TenantPresentationConfiguration',
          }),
        });
        cache.evict({
          id: cache.identify({
            __typename: 'SubBrand',
            id: selectedActiveBrandId,
          }),
        });
      },
    }
  );

  const [createOrganizationDisclaimers] =
    useCreateOrganizationLegalDisclaimersMutation({
      onCompleted: createSuccessFeedback(successMessage),
      onError: (err) => {
        showFeedback(
          'There was an error updating the legal disclaimers. Please try again.'
        );
        reportError('failed to create organization legal disclaimers', err);
      },
    });

  const [updateOrganizationDisclaimers] =
    useUpdateOrganizationLegalDisclaimersMutation({
      onCompleted: createSuccessFeedback(successMessage),
      onError: (err) => {
        showFeedback(
          'There was an error updating the legal disclaimers. Please try again.'
        );
        reportError('failed to update organization legal disclaimers', err);
      },
    });

  const formMethods = useForm<LegalDisclaimersFormShape>({
    defaultValues: defaultDisclaimerConfiguration,
  });

  const {
    reset,
    formState: { isSubmitting },
  } = formMethods;

  useEffect(() => {
    if (isOrganizationManaged) {
      reset(
        getFormDisclaimers(
          defaultDisclaimerConfiguration,
          organizationDisclosures
        )
      );
      return;
    }

    const subBrandDisclosures = subBrandDisclosuresMap[selectedActiveBrandId];
    reset(
      getFormDisclaimers(
        defaultDisclaimerConfiguration,
        tenantDisclosures,
        subBrandDisclosures
      )
    );
  }, [
    isOrganizationManaged,
    organizationDisclosures,
    selectedActiveBrandId,
    reset,
    subBrandDisclosuresMap,
    tenantDisclosures,
    defaultDisclaimerConfiguration,
  ]);

  const handleFormSubmit = useCallback(
    (formData: LegalDisclaimersFormShape) => {
      if (isOrganizationManaged) {
        if (organizationDisclosures) {
          const input = getUpdateOrganizationDisclaimersInput(
            organizationDisclosures.id,
            formData,
            defaultDisclaimerConfiguration
          );
          return updateOrganizationDisclaimers({
            variables: { input },
            refetchQueries: [Operations.Query.LegalDisclaimers],
            awaitRefetchQueries: true,
          });
        } else {
          const input = getCreateOrganizationDisclaimersInput(
            formData,
            defaultDisclaimerConfiguration
          );
          return createOrganizationDisclaimers({
            variables: { input },
            refetchQueries: [Operations.Query.LegalDisclaimers],
            awaitRefetchQueries: true,
          });
        }
      }

      const subBrandDisclaimerId =
        subBrandDisclosuresMap[selectedActiveBrandId]?.id;

      if (selectedActiveBrandId) {
        if (subBrandDisclaimerId) {
          const input = getUpdateSubBrandDisclaimersInput(
            subBrandDisclaimerId,
            formData,
            tenantDisclosures,
            defaultDisclaimerConfiguration
          );
          return updateSubBrandDisclaimers({
            variables: { input },
          });
        } else {
          const input = getCreateSubBrandDisclaimersInput(
            formData,
            selectedActiveBrandId,
            tenantDisclosures,
            defaultDisclaimerConfiguration
          );
          return createSubBrandDisclaimers({
            variables: { input },
          });
        }
      } else {
        const disclaimerId = tenantDisclosures?.id;

        if (!disclaimerId) throw new Error('disclaimerId not available');

        const input = getUpdateTenantDisclaimersInput(
          disclaimerId,
          formData,
          defaultDisclaimerConfiguration
        );
        return updateTenantDisclaimers({
          variables: { input },
        });
      }
    },
    [
      createOrganizationDisclaimers,
      createSubBrandDisclaimers,
      defaultDisclaimerConfiguration,
      isOrganizationManaged,
      organizationDisclosures,
      selectedActiveBrandId,
      subBrandDisclosuresMap,
      tenantDisclosures,
      updateOrganizationDisclaimers,
      updateSubBrandDisclaimers,
      updateTenantDisclaimers,
    ]
  );
  const onSubmit = formMethods.handleSubmit(async (formData) => {
    await handleFormSubmit(formData);
  });

  const placeholders = useMemo(() => {
    if (!isOrganizationManaged && selectedActiveBrandId) {
      return getFormDisclaimers(
        defaultDisclaimerConfiguration,
        tenantDisclosures
      );
    }
    return getFormDisclaimers(defaultDisclaimerConfiguration);
  }, [
    defaultDisclaimerConfiguration,
    isOrganizationManaged,
    selectedActiveBrandId,
    tenantDisclosures,
  ]);

  return (
    <FormProvider {...formMethods}>
      <SubpageLayout heading="Legal disclaimers for presentations">
        <Grid container>
          <Grid item lg={8} sm={12}>
            <Stack spacing={2}>
              {isOrganizationManaged && (
                <Callout severity="info-high">
                  Your organization&apos;s legal disclaimers take precedence
                  over tenant and sub-brand disclaimers. Changes apply to
                  presentations across every member tenant and sub-brand.
                  {!canEditOrganizationDisclaimers &&
                    ' Only authorized administrators in the organization’s parent tenant can make changes.'}
                </Callout>
              )}
              <Card variant="outlined" sx={{ p: 2 }}>
                {!isOrganizationManaged && !isEmpty(subBrands) && (
                  <Stack spacing={2} pb={2}>
                    <Stack>
                      <Typography variant="title-s">Sub-brand</Typography>
                      <Typography variant="body-xs">
                        Select the sub-brand you would like to specify
                        disclaimers for
                      </Typography>
                    </Stack>
                    <SelectInput
                      disabled={isSubmitting}
                      options={subBrandOptions}
                      showEmptyValue={false}
                      startAdornment={
                        <InputAdornment
                          sx={{ backgroundColor: 'white' }}
                          position="start"
                        >
                          <Briefcase02Icon size={16} />
                        </InputAdornment>
                      }
                      value={selectedActiveBrandId}
                      onChange={(e) => setSelectedActiveBrand(e.target.value)}
                      label="Active brand"
                      hideLabel
                    />
                  </Stack>
                )}
                <LegalDisclaimersForm
                  onSubmit={onSubmit}
                  defaultDisclaimerConfiguration={placeholders}
                  readOnly={isReadOnly}
                />
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </SubpageLayout>
    </FormProvider>
  );
}
